import Head from 'next/head'
import Seo, { SITE_URL } from '@comp/seo'
import JsonLd from '@comp/structured-data'
import Layout from '@comp/layout'
import ProductExperience from '@comp/product/product-experience'
import type { Capability } from '@comp/product/product-capabilities'
import type { Step } from '@comp/product/product-how-it-works'

const protocols = [
  'SCIM',
  'LDAP',
  'Active Directory',
  'Azure AD',
  'Okta',
  'Workday',
  'Google Workspace',
  'ServiceNow',
  'BambooHR',
  'Custom REST',
]

interface HubContent {
  seoTitle: string
  seoDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  capsEyebrow: string
  capsTitle: [string, string]
  capsBody: string
  capabilities: Capability[]
  howEyebrow: string
  howTitle: string
  howBody: string
  steps: Step[]
  intEyebrow: string
  intTitle: [string, string]
  intBody: string
  brochureCopy: string
}

const content: Record<'en' | 'tr', HubContent> = {
  en: {
    seoTitle: 'Monosync: Identity Governance & Administration | Monofor',
    seoDescription:
      "Monosync is Monofor's IGA platform: directory sync, entitlement mapping, access reviews, and compliance reporting, across every identity store.",
    heroEyebrow: 'Identity Governance & Administration',
    heroTitle: ['People move.', 'Access follows.'],
    heroBody:
      'Keep identities and permissions aligned as your organization changes. Connect your directories, govern access, and make every change traceable.',
    primaryCta: 'Start free trial →',
    secondaryCta: 'Download product brief',
    capsEyebrow: 'Capabilities',
    capsTitle: ['Everything you need', 'for modern IGA.'],
    capsBody:
      'Monosync gives compliance and IT one tool (discovery, certification, policy, and reporting) without forcing a directory migration.',
    capabilities: [
      {
        title: 'Directory Sync',
        description:
          'Continuous, bidirectional sync between AD, LDAP, HR systems, and every SaaS app you run.',
      },
      {
        title: 'Entitlement Mapping',
        description:
          'Discover and catalog every entitlement across every connected system in one place.',
      },
      {
        title: 'Access Reviews',
        description:
          'Manager-led and automated reviews on the cadence your auditors expect.',
      },
      {
        title: 'Separation of Duties',
        description:
          'Policy-as-code rules that prevent toxic permission combinations before they happen.',
      },
      {
        title: 'Audit Trails',
        description:
          'Immutable, tamper-evident logs for every grant, change, and revocation.',
      },
      {
        title: 'Compliance Reports',
        description:
          'Pre-built reports for SOC 2, ISO 27001, KVKK, GDPR, and PCI-DSS.',
      },
    ],
    howEyebrow: 'How it works',
    howTitle: 'From access chaos to audit-ready, on your timeline.',
    howBody:
      'Most teams pass their next access audit on Monosync within the first cycle of certification.',
    steps: [
      {
        title: 'Discover',
        description:
          'Connect Monosync to every identity store and learn what access exists today, across every system.',
      },
      {
        title: 'Govern',
        description:
          'Define policies, owners, and review cadences from one console. Push changes back to every connected source.',
      },
      {
        title: 'Certify',
        description:
          'Continuous certification with one-click attestation and audit-ready exports.',
      },
    ],
    intEyebrow: 'Integrations',
    intTitle: ['Reads from, and writes to,', 'every system you run.'],
    intBody:
      'Monosync acts as the governance plane across your existing identity stores. No replacement, no rip-and-replace.',
    brochureCopy:
      "Get the auditor's view: policy engine, attestation flow, and compliance framework coverage.",
  },
  tr: {
    seoTitle: 'Monosync: Kimlik Yönetişimi ve İdaresi (IGA) | Monofor',
    seoDescription:
      "Monosync, Monofor'un IGA platformudur: dizin senkronizasyonu, yetki haritalama, erişim gözden geçirmeleri ve uyum raporlaması, tüm kimlik depolarında.",
    heroEyebrow: 'Kimlik Yönetişimi ve İdaresi',
    heroTitle: ['Roller değişir.', 'Erişim uyum sağlar.'],
    heroBody:
      'Kurumunuz değişirken kimlikleri ve yetkileri uyumlu tutun. Dizinlerinizi bağlayın, erişimi yönetin ve her değişikliği izlenebilir kılın.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Ürün dokümanını indir',
    capsEyebrow: 'Yetenekler',
    capsTitle: ['Modern IGA için', 'ihtiyacınız olan her şey.'],
    capsBody:
      'Monosync; keşif, sertifikasyon, politika ve raporlamayı uyum ve BT ekiplerine tek araçta sunar, dizin taşımaya zorlamadan.',
    capabilities: [
      {
        title: 'Dizin Senkronizasyonu',
        description:
          'AD, LDAP, İK sistemleri ve çalıştırdığınız her SaaS uygulaması arasında sürekli, çift yönlü senkronizasyon.',
      },
      {
        title: 'Yetki Haritalama',
        description:
          'Bağlı tüm sistemlerdeki her yetkiyi tek yerde keşfedin ve kataloglayın.',
      },
      {
        title: 'Erişim Gözden Geçirmeleri',
        description:
          'Denetçilerinizin beklediği düzende, yönetici onaylı ve otomatik gözden geçirmeler.',
      },
      {
        title: 'Görevler Ayrılığı (SoD)',
        description:
          'Tehlikeli yetki kombinasyonlarını daha oluşmadan engelleyen politika kuralları.',
      },
      {
        title: 'Denetim İzleri',
        description:
          'Her yetki verme, değişiklik ve geri alma için değiştirilemez, kurcalamaya karşı korumalı loglar.',
      },
      {
        title: 'Uyum Raporları',
        description:
          'SOC 2, ISO 27001, KVKK, GDPR ve PCI-DSS için hazır rapor şablonları.',
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Erişim karmaşasından denetime hazır düzene, sizin takviminizle.',
    howBody:
      'Çoğu ekip ilk sertifikasyon döngüsünde bir sonraki erişim denetimini Monosync ile geçiyor.',
    steps: [
      {
        title: 'Keşfedin',
        description:
          'Monosync’i tüm kimlik depolarına bağlayın ve bugün hangi erişimlerin var olduğunu tüm sistemlerde öğrenin.',
      },
      {
        title: 'Yönetin',
        description:
          'Politikaları, sahipleri ve gözden geçirme düzenini tek konsoldan tanımlayın. Değişiklikleri bağlı her kaynağa geri yazın.',
      },
      {
        title: 'Belgeleyin',
        description:
          'Tek tıkla onay ve denetime hazır dışa aktarımlarla sürekli sertifikasyon.',
      },
    ],
    intEyebrow: 'Entegrasyonlar',
    intTitle: ['Çalıştırdığınız her sistemden okur,', 'her sisteme yazar.'],
    intBody:
      'Monosync, mevcut kimlik depolarınızın üzerinde yönetişim katmanı olarak çalışır. Değiştirme yok, söküp yeniden kurma yok.',
    brochureCopy:
      'Denetçi gözüyle inceleyin: politika motoru, onay akışı ve uyum çerçevesi kapsamı.',
  },
}

type Props = { locale: string }

export default function MonosyncPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  const basePath = locale === 'tr' ? '/tr' : ''
  return (
    <Layout>
      <Seo
        title={c.seoTitle}
        description={c.seoDescription}
        path={`${basePath}/sync`}
      />
      <Head>
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}/sync`} />
        <link rel="alternate" hrefLang="tr" href={`${SITE_URL}/tr/sync`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/sync`} />
      </Head>
      <JsonLd
        id="product"
        data={{
          '@type': 'SoftwareApplication',
          name: 'Monosync',
          applicationCategory: 'SecurityApplication',
          operatingSystem: 'Web (cloud or self-hosted)',
          description:
            "Monofor's IGA platform: directory sync, entitlement mapping, access reviews, and compliance reporting, across every identity store.",
          url: 'https://monofor.com/sync',
          publisher: { '@type': 'Organization', name: 'Monofor' },
        }}
      />
      <ProductExperience product="sync" lang={locale === 'tr' ? 'tr' : 'en'} content={c} protocols={protocols} />
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
